import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import DealNotificationModel, { DealNotificationUtils } from "../../viewModels/Notification/DealNotificationModel";
import globalSlice from "../../containers/App/reducer";
import dealPageSlice from "../../containers/DealPage/reducer";
import { OFFICE_TYPES } from "../../dto/Company/CompanySettingsDto";
import { CreateDealNotificationFormInput, OpenDealNotificationModalPayload } from "./types";
import { DealQuestionContextDto } from "../../dto/Notification/DealNotificationDto";
import { GetDealQuestionContextRequestDto } from "../../api/apiServices/backOffice/controllers/dealQuestions/dealQuestions.requestsDto";

const initialState: {
  open: boolean;
  loading: boolean;
  dealNotifications: Array<DealNotificationModel>;
  dealId: number;
  managerId: number;
  adminOfficeType?: OFFICE_TYPES;
  context?: DealQuestionContextDto;
  contextLoading: boolean;
  contextError: boolean;
  pendingRequestId?: string;
  createdRequestId?: string;
} = {
  open: false,
  loading: false,
  contextLoading: false,
  contextError: false,
  dealNotifications: [],
  dealId: undefined!,
  managerId: undefined!,
  adminOfficeType: undefined,
};

const dealNotificationModalSlice = createSlice({
  name: "dealNotificationModal",
  initialState,
  reducers: {
    openModal(state, action: PayloadAction<OpenDealNotificationModalPayload>) {
      state.context = undefined;
      state.contextError = false;
      state.contextLoading = false;
      state.pendingRequestId = undefined;
      state.open = true;
      state.dealNotifications = action.payload.dealNotifications;
      state.dealId = action.payload.dealId;
      state.managerId = action.payload.managerId;
      state.adminOfficeType = action.payload.adminOfficeType;
    },
    closeModal(state) {
      state.open = false;
      state.dealNotifications = [];
      state.dealId = undefined!;
      state.managerId = undefined!;
      state.adminOfficeType = undefined;
      state.context = undefined;
      state.pendingRequestId = undefined;
    },
    loadContext(state, _action: PayloadAction<GetDealQuestionContextRequestDto>) {
      state.contextLoading = true;
      state.contextError = false;
    },
    loadContextSuccess(state, action: PayloadAction<DealQuestionContextDto>) {
      if (state.dealId !== action.payload.dealId || (state.adminOfficeType && state.adminOfficeType !== action.payload.officeType)) return;
      state.contextLoading = false;
      state.context = action.payload;
    },
    loadContextReject(state, action: PayloadAction<GetDealQuestionContextRequestDto>) {
      if (state.dealId !== action.payload.dealId || state.adminOfficeType !== action.payload.officeType) return;
      state.contextLoading = false;
      state.contextError = true;
    },
    createDealNotification(state, action: PayloadAction<CreateDealNotificationFormInput>) {
      state.loading = true;
      state.pendingRequestId = action.payload.requestId;
    },
    createDealNotificationSuccess(state, action: PayloadAction<DealNotificationModel>) {
      state.createdRequestId = action.payload.requestId;
      state.loading = false;
      if (state.pendingRequestId !== action.payload.requestId) return;
      if (state.pendingRequestId === action.payload.requestId && state.dealId === action.payload.deal.id) {
        if (!state.dealNotifications.some((x) => DealNotificationUtils.isSameQuestion(x, action.payload))) {
          state.dealNotifications = [action.payload, ...state.dealNotifications];
        }
      }
      state.pendingRequestId = undefined;
    },
    createDealNotificationReject(state, _action: PayloadAction<string>) {
      state.loading = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(dealPageSlice.actions.updateDealSuccess, (state, action) => {
      if (state.open && !state.adminOfficeType && state.dealId === action.payload.id) {
        state.dealNotifications = action.payload.dealNotifications || [];
      }
    });
    builder.addCase(globalSlice.actions.sendDealNotificationResponseSuccess, (state, action) => {
      const index = state.dealNotifications.findIndex((x) => DealNotificationUtils.isSameQuestion(x, action.payload));
      if (index !== -1) {
        state.dealNotifications[index] = action.payload;
      }
    });
    builder.addCase(globalSlice.actions.closeDealNotificationSuccess, (state, action) => {
      const index = state.dealNotifications.findIndex((x) => DealNotificationUtils.isSameQuestion(x, action.payload));
      if (index !== -1) {
        state.dealNotifications[index] = action.payload;
      }
    });
  },
});

export default dealNotificationModalSlice;
